import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { API, useAuth } from "@/App";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent } from "@/components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { Eye, EyeOff, Phone, Lock, Mail, User, ArrowLeft, Store, Building2, CheckCircle } from "lucide-react";
import { useFirebaseOTP } from "@/hooks/useFirebaseOTP";

const LOGO_URL = "https://customer-assets.emergentagent.com/job_097dcc50-4812-4b75-a884-fb87f03fa7a9/artifacts/6cfgy4bw_logo%20%281%29.webp";

const SellerRegister = () => {
  const navigate = useNavigate();
  const { login } = useAuth();
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [idToken, setIdToken] = useState(null);
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    mobile: "",
    otp: "",
    password: "",
    business_name: "",
    business_type: "",
    gst_number: "",
    address: "",
  });

  const otp = useFirebaseOTP();
  // Firebase optional — if the web SDK config isn't populated on the deployed build,
  // fall back to legacy password-only registration (no OTP required).
  const firebaseConfigured = !!process.env.REACT_APP_FIREBASE_API_KEY;

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const sendOTP = async () => {
    await otp.sendOTP(formData.mobile);
  };

  const verifyOTP = async () => {
    const token = await otp.verifyOTP(formData.otp);
    if (token) {
      setIdToken(token);
      toast.success("Mobile verified!");
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (formData.password.length < 6) {
      toast.error("Password must be at least 6 characters");
      return;
    }
    if (firebaseConfigured && !idToken) {
      toast.error("Please verify your mobile number with OTP first");
      return;
    }
    setLoading(true);
    try {
      if (firebaseConfigured && idToken) {
        // Firebase path
        const res = await axios.post(`${API}/auth/firebase`, {
          id_token: idToken,
          purpose: "register_seller",
          name: formData.name,
          email: formData.email || null,
          password: formData.password,
          business_name: formData.business_name,
        });
        login(res.data.seller, res.data.token);
      } else {
        // Legacy path (no Firebase — works even if env vars missing on prod)
        const res = await axios.post(`${API}/auth/register/seller`, {
          name: formData.name,
          email: formData.email,
          mobile: formData.mobile,
          password: formData.password,
          business_name: formData.business_name,
          business_type: formData.business_type,
          gst_number: formData.gst_number,
          address: formData.address,
        });
        login(res.data.seller, res.data.token);
      }
      toast.success("Registration successful! Awaiting admin approval.");
      navigate("/seller/dashboard");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Registration failed");
    }
    setLoading(false);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary-50 via-white to-secondary-50 py-8 px-4">
      <div className="max-w-2xl mx-auto">
        <div className="text-center mb-8">
          <Link to="/">
            <img src={LOGO_URL} alt="M Ventures" className="h-12 mx-auto mb-4" />
          </Link>
          <h1 className="font-heading text-2xl font-bold text-gray-900">Become a Seller</h1>
          <p className="text-gray-500">Start selling on M Ventures marketplace</p>
        </div>

        <Card className="border-0 shadow-xl">
          <CardContent className="p-6">
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="name">Full Name</Label>
                  <div className="relative mt-1">
                    <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <Input
                      id="name"
                      name="name"
                      placeholder="Enter your name"
                      value={formData.name}
                      onChange={handleChange}
                      className="pl-10"
                      required
                      data-testid="seller-name-input"
                    />
                  </div>
                </div>
                <div>
                  <Label htmlFor="email">Email</Label>
                  <div className="relative mt-1">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <Input
                      id="email"
                      name="email"
                      type="email"
                      placeholder="Enter email"
                      value={formData.email}
                      onChange={handleChange}
                      className="pl-10"
                      required
                      data-testid="seller-email-input"
                    />
                  </div>
                </div>
              </div>

              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="mobile">Mobile Number</Label>
                  <div className="relative mt-1 flex gap-2">
                    <div className="relative flex-1">
                      <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                      <Input
                        id="mobile"
                        name="mobile"
                        type="tel"
                        placeholder="10-digit mobile"
                        value={formData.mobile}
                        onChange={handleChange}
                        className="pl-10"
                        maxLength={10}
                        required
                        disabled={!!idToken}
                        data-testid="seller-mobile-input"
                      />
                    </div>
                    {firebaseConfigured && !otp.otpSent && !idToken && (
                      <Button
                        type="button"
                        size="sm"
                        variant="outline"
                        onClick={sendOTP}
                        disabled={otp.sending || !formData.mobile}
                        data-testid="seller-send-otp-btn"
                      >
                        {otp.sending ? "..." : "Send OTP"}
                      </Button>
                    )}
                    {firebaseConfigured && idToken && (
                      <div className="flex items-center text-green-600 text-sm px-2">
                        <CheckCircle className="w-4 h-4 mr-1" /> Verified
                      </div>
                    )}
                  </div>
                  {firebaseConfigured && otp.otpSent && !idToken && (
                    <div className="mt-2 flex gap-2">
                      <Input
                        name="otp"
                        placeholder="Enter OTP"
                        value={formData.otp}
                        onChange={handleChange}
                        maxLength={6}
                        data-testid="seller-otp-input"
                      />
                      <Button
                        type="button"
                        size="sm"
                        onClick={verifyOTP}
                        disabled={otp.verifying || formData.otp.length !== 6}
                        data-testid="seller-verify-otp-btn"
                      >
                        {otp.verifying ? "..." : "Verify"}
                      </Button>
                    </div>
                  )}
                </div>
                <div>
                  <Label htmlFor="password">Password</Label>
                  <div className="relative mt-1">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <Input
                      id="password"
                      name="password"
                      type={showPassword ? "text" : "password"}
                      placeholder="Create password"
                      value={formData.password}
                      onChange={handleChange}
                      className="pl-10 pr-10"
                      required
                      data-testid="seller-password-input"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400"
                    >
                      {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                </div>
              </div>

              <div className="border-t pt-4">
                <h3 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
                  <Building2 className="w-5 h-5 text-primary-600" />
                  Business Details
                </h3>
              </div>

              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="business_name">Business Name</Label>
                  <div className="relative mt-1">
                    <Store className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <Input
                      id="business_name"
                      name="business_name"
                      placeholder="Your business name"
                      value={formData.business_name}
                      onChange={handleChange}
                      className="pl-10"
                      required
                      data-testid="seller-business-name-input"
                    />
                  </div>
                </div>
                <div>
                  <Label>Business Type</Label>
                  <Select
                    value={formData.business_type}
                    onValueChange={(v) => setFormData({ ...formData, business_type: v })}
                  >
                    <SelectTrigger className="mt-1" data-testid="seller-business-type">
                      <SelectValue placeholder="Select type" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="individual">Individual</SelectItem>
                      <SelectItem value="proprietorship">Proprietorship</SelectItem>
                      <SelectItem value="partnership">Partnership</SelectItem>
                      <SelectItem value="pvt_ltd">Private Limited</SelectItem>
                      <SelectItem value="llp">LLP</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div>
                <Label htmlFor="gst_number">GST Number (Optional)</Label>
                <Input
                  id="gst_number"
                  name="gst_number"
                  placeholder="Enter GST number"
                  value={formData.gst_number}
                  onChange={handleChange}
                />
              </div>

              <div>
                <Label htmlFor="address">Business Address</Label>
                <Textarea
                  id="address"
                  name="address"
                  placeholder="Enter complete business address"
                  value={formData.address}
                  onChange={handleChange}
                  required
                />
              </div>

              <div className="p-4 bg-blue-50 rounded-xl text-sm text-blue-700">
                <p>
                  By registering, you agree to our seller terms. Your account will be
                  reviewed by our team before activation.
                </p>
              </div>

              <Button
                type="submit"
                className="w-full bg-secondary-500 !text-primary-900 hover:bg-secondary-400 rounded-full py-6"
                disabled={loading || (firebaseConfigured && !idToken)}
                data-testid="seller-register-submit-btn"
              >
                {loading ? "Submitting..." : (firebaseConfigured && !idToken) ? "Verify mobile with OTP first" : "Submit Registration"}
              </Button>
            </form>

            {/* Invisible reCAPTCHA container for Firebase */}
            <div id="recaptcha-container"></div>

            <p className="text-center mt-6 text-gray-500">
              Already a seller?{" "}
              <Link to="/login" className="text-primary-600 font-medium hover:underline">
                Sign in
              </Link>
            </p>
          </CardContent>
        </Card>

        <div className="text-center mt-6">
          <Link to="/" className="text-gray-500 hover:text-gray-700 inline-flex items-center gap-2">
            <ArrowLeft className="w-4 h-4" />
            Back to Home
          </Link>
        </div>
      </div>
    </div>
  );
};

export default SellerRegister;
